'use client';

import React, { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  FEEDBACK_DETAILS_QUESTION,
  FEEDBACK_FORM_SLUG,
  FEEDBACK_NEGATIVE_OPTIONS,
  FEEDBACK_NEGATIVE_QUESTION,
  FEEDBACK_POSITIVE_OPTIONS,
  FEEDBACK_POSITIVE_QUESTION,
  FEEDBACK_POSITIVE_THRESHOLD,
  FEEDBACK_QUESTION,
} from './config';
import FeedbackDetailsStep from './FeedbackDetailsStep';
import FeedbackNavButtons from './FeedbackNavButtons';
import FeedbackOptionsStep from './FeedbackOptionsStep';
import FeedbackStarRating from './FeedbackStarRating';
import FeedbackStepHeader from './FeedbackStepHeader';
import FeedbackThankYou from './FeedbackThankYou';
import type { FormSubmitResponse } from '@/types/formBuilder';
import type {
  FeedbackFormValues,
  FeedbackStep,
} from './types';

const defaultValues: FeedbackFormValues = {
  reasons: [],
  details: '',
};

const FooterFeedbackWidget = () => {
  const methods = useForm<FeedbackFormValues>({
    defaultValues,
    mode: 'onTouched',
    shouldUnregister: false,
  });

  const { getValues, reset, setError, clearErrors } = methods;

  const [step, setStep] = useState<FeedbackStep>('rating');
  const [rating, setRating] = useState<number | null>(null);
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);

  const isPositiveRating =
    rating !== null && rating > FEEDBACK_POSITIVE_THRESHOLD;

  const stepOptions = useMemo(
    () =>
      isPositiveRating ? FEEDBACK_POSITIVE_OPTIONS : FEEDBACK_NEGATIVE_OPTIONS,
    [isPositiveRating],
  );

  const stepQuestion = isPositiveRating
    ? FEEDBACK_POSITIVE_QUESTION
    : FEEDBACK_NEGATIVE_QUESTION;

  const handleRatingSelect = (value: number) => {
    const ratingChanged = rating !== null && rating !== value;
    setRating(value);

    if (ratingChanged) {
      reset(defaultValues);
    }

    setStep('options');
  };

  const handleBackFromOptions = () => {
    clearErrors();
    setRating(null);
    setHoveredRating(null);
    setSelectedReasons([]);
    reset(defaultValues);
    setStep('rating');
  };

  const handleBackFromDetails = () => {
    clearErrors();
    setStep('options');
  };

  const handleNextFromOptions = () => {
    const selectedReasons = getValues('reasons') || [];

    if (selectedReasons.length === 0) {
      setError('reasons', {
        type: 'manual',
        message: 'Seleziona almeno un’opzione per continuare.',
      });
      return;
    }

    clearErrors('reasons');
    setSelectedReasons(selectedReasons);
    setStep('details');
  };

  const handleSubmit = async () => {
    if (rating === null || isSubmitting) return;

    const reasons = getValues('reasons') || selectedReasons;
    const details = getValues('details') || '';

    clearErrors('details');
    setIsSubmitting(true);

    try {
      const response = await fetch(
        `/api/forms/${encodeURIComponent(FEEDBACK_FORM_SLUG)}/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            data: {
              rating,
              reasons,
              details,
            },
          }),
        },
      );

      const body = (await response
        .json()
        .catch(() => ({ success: false }))) as FormSubmitResponse;

      if (!response.ok || !body.success) {
        setError('details', {
          type: 'server',
          message:
            (!body.success && body.message) ||
            'Si è verificato un errore durante l\u2019invio. Riprova.',
        });
        return;
      }

      setStep('thankYou');
    } catch {
      setError('details', {
        type: 'server',
        message:
          'Impossibile inviare il modulo. Controlla la connessione e riprova.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (step === 'options') {
      handleNextFromOptions();
      return;
    }

    if (step === 'details') {
      void handleSubmit();
    }
  };

  return (
    <FormProvider {...methods}>
      {step === 'thankYou' ? (
        <div className="bg-white p-4 w-fit mx-auto it-customer-rating it-customer-rating-expanded">
          <FeedbackThankYou />
        </div>
      ) : step === 'rating' ? (
        <div className="bg-white p-4 w-fit mx-auto it-customer-rating">
          <h2 className="it-customer-rating-title mb-3">{FEEDBACK_QUESTION}</h2>
          <FeedbackStarRating
            rating={rating}
            hoveredRating={hoveredRating}
            onSelect={handleRatingSelect}
            onHover={setHoveredRating}
          />
        </div>
      ) : (
        <div className="bg-white p-4 w-fit mx-auto it-customer-rating it-customer-rating-expanded">
          <FeedbackStepHeader
            question={FEEDBACK_QUESTION}
            rating={rating!}
            hoveredRating={hoveredRating}
            onRatingSelect={handleRatingSelect}
            onRatingHover={setHoveredRating}
            stepTitle={
              step === 'options' ? stepQuestion : FEEDBACK_DETAILS_QUESTION
            }
            stepIndicator={step === 'options' ? '1/2' : '2/2'}
            showStepMeta
          />

          {step === 'options' ? (
            <FeedbackOptionsStep
              options={stepOptions}
              error={methods.formState.errors.reasons?.message}
            />
          ) : (
            <FeedbackDetailsStep
              error={methods.formState.errors.details?.message}
            />
          )}

          <div className="it-feedback-actions">
            <div className="it-feedback-actions-shadow" />
            <FeedbackNavButtons
              onBack={
                step === 'options'
                  ? handleBackFromOptions
                  : handleBackFromDetails
              }
              onNext={handleNext}
              isSubmitting={isSubmitting}
            />
          </div>
        </div>
      )}
    </FormProvider>
  );
};

export default FooterFeedbackWidget;
